import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Rating from "./Rating";
import { FaTrash, FaEdit, FaEllipsisV } from "react-icons/fa";
import { useDeleteProductCreatorMutation } from "../slices/productsApiSlice.js";
import { toast } from "sonner";
import { languageFlags } from "../constants.js";
import UserAvatar from "./UserAvatar.jsx";

function Product({
  product,
  isCreator,
  displayPrice,
  currency,
  creatorCategories = [],
  onAssignToCategory,
  onRemoveFromCategory,
  onCreateCategoryAndAssign,
  className = "",
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const productUrl = isCreator && product.isVisible === false
    ? `/profile/creator/${product._id}/edit`
    : `/product/${product._id}`;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeletingProduct, setIsDeletingProduct] = useState(false);
  const [categoryEdges, setCategoryEdges] = useState({ left: 0, right: 0 });
  const categoryDrag = useRef(null);
  const [keywordsMoving, setKeywordsMoving] = useState(false);
  const keywordIdleTimer = useRef(null);
  const showKeywordFeedback = () => {
    setKeywordsMoving(true);
    clearTimeout(keywordIdleTimer.current);
    keywordIdleTimer.current = setTimeout(() => setKeywordsMoving(false), 350);
  };
  useEffect(() => () => clearTimeout(keywordIdleTimer.current), []);
  const categoriesRef = useRef(null);
  const isFreeProduct = Boolean(product?.isFree) || Number(product?.price) <= 0;
  const fallbackPriceNumber = Number(product?.price ?? 0);
  const resolvedDisplayPrice =
    displayPrice ?? (Number.isFinite(fallbackPriceNumber) ? fallbackPriceNumber.toFixed(2) : "0.00");
  const ownerUrl = String(product?.ownerUrl || "").startsWith("/")
    ? product.ownerUrl
    : `/${String(product?.ownerUrl || "")}`;
  const ownerName = String(product?.ownerName || "");
  const ownerDisplayName = ownerName.length > 18 ? `${ownerName.slice(0, 15)}...` : ownerName;
  const languageList = Array.isArray(product?.languages) ? product.languages : [];
  const mappedLanguages = languageList.filter((lang) => Boolean(languageFlags[lang]));
  const fallbackLanguages = languageList.filter((lang) => !languageFlags[lang]);
  const visibleLanguages = [
    ...mappedLanguages.slice(0, 2),
    ...fallbackLanguages.slice(0, Math.max(0, 2 - mappedLanguages.length)),
  ].slice(0, 2);
  const hasMoreLanguages = languageList.length > 2;
  const hiddenLanguages = (() => {
    if (!hasMoreLanguages) return [];

    const remainingVisible = [...visibleLanguages];
    return languageList.filter((lang) => {
      const idx = remainingVisible.findIndex((entry) => entry === lang);
      if (idx >= 0) {
        remainingVisible.splice(idx, 1);
        return false;
      }
      return true;
    });
  })();

  const assignedCategoryIds = useMemo(
    () =>
      new Set(
        (creatorCategories || [])
          .filter(
            (entry) =>
              Array.isArray(entry?.productIds) &&
              entry.productIds.some((id) => String(id) === String(product?._id))
          )
          .map((entry) => String(entry._id))
      ),
    [creatorCategories, product?._id]
  );

  const [deleteProduct] = useDeleteProductCreatorMutation();
  const visibilityBadgeClass = product?.isVisible
    ? "bg-emerald-100 text-emerald-700"
    : "bg-amber-100 text-amber-700";
  const pricingBadgeClass = isFreeProduct
    ? "bg-sky-100 text-sky-700"
    : "bg-slate-100 text-slate-700";

  const deleteHandler = async (productId) => {
    try {
      setIsDeletingProduct(true);
      const result = await deleteProduct(productId).unwrap();
      toast.success(result.message);
      setShowDeleteModal(false);
    } catch (err) {
      toast.error(err?.data?.message || "Failed to delete product");
    } finally {
      setIsDeletingProduct(false);
    }
  };

  const addableCategories = (creatorCategories || []).filter(
    (entry) => !assignedCategoryIds.has(String(entry._id))
  );
  const removableCategories = (creatorCategories || []).filter((entry) =>
    assignedCategoryIds.has(String(entry._id))
  );

  useEffect(() => {
    const el = categoriesRef.current;
    if (!el) return;

    const measure = () => {
      const max = el.scrollWidth - el.clientWidth;
      setCategoryEdges(max > 1 ? {
        left: Math.max(0, 1 - Math.max(0, el.scrollLeft) / 24),
        right: Math.max(0, 1 - Math.max(0, max - el.scrollLeft) / 24),
      } : { left: 0, right: 0 });
    };

    measure();
    el.addEventListener("scroll", measure, { passive: true });
    const resizeObserver = new ResizeObserver(measure);
    resizeObserver.observe(el);
    Array.from(el.children).forEach((child) => resizeObserver.observe(child));

    return () => {
      resizeObserver.disconnect();
      el.removeEventListener("scroll", measure);
    };
  }, [product?.category]);

  return (
    <>
      <article
      className={`my-3 flex h-auto md:h-[27rem] flex-col overflow-visible rounded-2xl border border-emerald-100 bg-white shadow-[0_2px_8px_rgba(15,23,42,0.06)] transition-shadow duration-200 hover:shadow-[0_12px_24px_rgba(15,23,42,0.12)] ${className}`}
      >
      <Link to={productUrl} className="block shrink-0 md:shrink overflow-hidden rounded-t-2xl border-b border-slate-100 bg-slate-50">
        <div className="h-auto md:h-56">
          {product.images?.[0] ? (
            <img
              src={product.images[0]}
              alt={product.name}
              className="block h-auto w-full md:h-full md:object-cover md:object-[center_35%]"
            />
          ) : (
            <div className="flex h-52 md:h-full w-full items-center justify-center bg-gradient-to-br from-slate-100 via-slate-50 to-sky-100 text-sm font-semibold text-slate-500">
              No image available
            </div>
          )}
        </div>
      </Link>

      <div className="space-y-1 px-3 pb-2 pt-2">
        <Link to={productUrl} className="no-underline">
          <h3 className="product-title-two-line mb-0 text-xl font-semibold leading-tight text-slate-900">
            {product.name}
          </h3>
        </Link>

        {isCreator && (
          <div className="flex flex-wrap gap-1.5 pt-0.5">
            <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${visibilityBadgeClass}`}>
              {product?.isVisible ? "Visible" : "Hidden"}
            </span>
            <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${pricingBadgeClass}`}>
              {isFreeProduct ? "Free" : "Paid"}
            </span>
          </div>
        )}

        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-1">
            <Link to={ownerUrl} className="shrink-0 -mr-1">
              <UserAvatar
                user={{
                  name: product.ownerName,
                  profileImage: product.ownerImage,
                  userColor: product.userColor,
                }}
                size={33}
              />
            </Link>
            <Link
              to={ownerUrl}
              className="truncate text-[15px] font-medium text-slate-700 no-underline hover:text-sky-700"
              title={ownerName}
            >
              {ownerDisplayName}
            </Link>
          </div>

          <div className="flex items-center gap-1">
            {visibleLanguages.map((lang) => {
              const flagSrc = languageFlags[lang];
              if (flagSrc) {
                return (
                <img
                  key={lang}
                  src={flagSrc}
                  alt={lang}
                  title={lang}
                  width="26"
                  height="18"
                  className="rounded-sm border border-slate-200 object-cover"
                />
                );
              }

              const fallback = String(lang).trim().slice(0, 2).toUpperCase() || "--";
              return (
                <span
                  key={lang}
                  title={lang}
                  className="inline-flex h-[18px] min-w-[26px] items-center justify-center rounded-sm border border-slate-200 bg-slate-100 px-1 text-[9px] font-semibold leading-none text-slate-600"
                >
                  {fallback}
                </span>
              );
            })}
            {hasMoreLanguages && (
              <span
                className="inline-flex h-[18px] w-[26px] items-center justify-center rounded-sm border border-slate-200 bg-slate-100 text-[10px] font-semibold leading-none text-slate-600"
                title={hiddenLanguages.join(", ")}
              >
                ...
              </span>
            )}
          </div>
        </div>

        <div className="relative">
          <div ref={categoriesRef} className="product-keywords flex flex-nowrap gap-1.5"
            tabIndex={0} role="region" aria-label="Product keywords"
            onScroll={showKeywordFeedback}
            onPointerDown={(event) => {
              if (event.pointerType === "mouse" && event.button !== 0) return;
              categoryDrag.current = { x: event.clientX, y: event.clientY, scroll: event.currentTarget.scrollLeft, type: event.pointerType };
              if (event.pointerType === "mouse") event.currentTarget.setPointerCapture(event.pointerId);
            }}
            onPointerMove={(event) => {
              if (!categoryDrag.current) return;
              const delta = categoryDrag.current.x - event.clientX;
              if (Math.abs(delta) > 3 && Math.abs(delta) > Math.abs(event.clientY - categoryDrag.current.y)) showKeywordFeedback();
              if (categoryDrag.current.type === "mouse") event.currentTarget.scrollLeft = categoryDrag.current.scroll + delta;
            }}
            onPointerUp={() => { categoryDrag.current = null; }}
            onPointerCancel={() => { categoryDrag.current = null; }}
            onLostPointerCapture={() => { categoryDrag.current = null; }}
          >
          {product.category && product.category.length > 0 ? (
            product.category.map((entry) => (
              <span
                key={entry}
                className="shrink-0 whitespace-nowrap rounded-full border border-slate-200 bg-slate-100 px-2.5 py-1 text-[13px] font-medium text-slate-700"
                title={entry}
              >
                {entry}
              </span>
            ))
          ) : (
            <span className="text-sm text-slate-500">No categories available</span>
          )}
          </div>
          <span aria-hidden="true" className="product-keyword-edge product-keyword-edge--left" style={{ opacity: keywordsMoving ? categoryEdges.left : 0 }} />
          <span aria-hidden="true" className="product-keyword-edge product-keyword-edge--right" style={{ opacity: keywordsMoving ? categoryEdges.right : 0 }} />
        </div>

        <div className="product-card-price-review">
          <p className="m-0 text-2xl font-semibold text-slate-800">
            {isFreeProduct ? "Free" : `${currency === "USD" ? "$" : ""}${resolvedDisplayPrice}`}
          </p>
          <div className="product-card-reviews">
            <Rating value={product.rating} text={Number(product.numReviews) > 0 ? `${product.numReviews} ${Number(product.numReviews) === 1 ? "review" : "reviews"}` : "No reviews"} />
          </div>
        </div>

        <div className="relative flex items-center justify-end gap-2">
          {isCreator && (
            <div className="flex items-center gap-1.5">
              <Link
                to={`/profile/creator/${product._id}/edit`}
                className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-sky-600 text-[13px] text-white no-underline hover:bg-sky-700"
                title="Edit product"
              >
                <FaEdit />
              </Link>
              <button
                type="button"
                className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-rose-600 text-[13px] text-white hover:bg-rose-700"
                onClick={() => setShowDeleteModal(true)}
                title="Delete product"
              >
                <FaTrash />
              </button>
              <button
                type="button"
                className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-emerald-600 text-[13px] text-white hover:bg-emerald-700"
                onClick={() => setMenuOpen((prev) => !prev)}
                title="Category actions"
              >
                <FaEllipsisV />
              </button>
            </div>
          )}

          {menuOpen && (
            <div className="absolute -right-3 top-8 z-50 w-56 rounded-xl border border-slate-200 bg-white p-2 shadow-xl">
              <p className="mb-1 px-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Add to...
              </p>
              {addableCategories.length > 0 ? (
                addableCategories.map((entry) => (
                  <button
                    key={`add-${entry._id}`}
                    type="button"
                    className="block w-full rounded-md px-2 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-50"
                    onClick={() => {
                      onAssignToCategory?.(product._id, entry._id);
                      setMenuOpen(false);
                    }}
                  >
                    {entry.name}
                  </button>
                ))
              ) : (
                <p className="px-2 py-1 text-sm text-slate-400">No categories available</p>
              )}

              <hr className="my-2 border-slate-200" />

              <p className="mb-1 px-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Remove from...
              </p>
              {removableCategories.length > 0 ? (
                removableCategories.map((entry) => (
                  <button
                    key={`remove-${entry._id}`}
                    type="button"
                    className="block w-full rounded-md px-2 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-50"
                    onClick={() => {
                      onRemoveFromCategory?.(product._id, entry._id);
                      setMenuOpen(false);
                    }}
                  >
                    {entry.name}
                  </button>
                ))
              ) : (
                <p className="px-2 py-1 text-sm text-slate-400">Not in any category</p>
              )}

              <hr className="my-2 border-slate-200" />
              <button
                type="button"
                className="block w-full rounded-md px-2 py-1.5 text-left text-sm font-medium text-sky-700 hover:bg-sky-50"
                onClick={() => {
                  onCreateCategoryAndAssign?.(product._id);
                  setMenuOpen(false);
                }}
              >
                Create new category
              </button>
            </div>
          )}
        </div>
      </div>
      </article>

      {showDeleteModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
          onClick={() => !isDeletingProduct && setShowDeleteModal(false)}
          role="presentation"
        >
          <div
            className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Delete product"
          >
            <h3 className="text-lg font-semibold text-slate-900">Delete Product</h3>
            <p className="mt-2 text-sm text-slate-600">
              Are you sure you want to delete this product? This action cannot be undone.
            </p>
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                disabled={isDeletingProduct}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => deleteHandler(product._id)}
                disabled={isDeletingProduct}
                className="rounded-xl bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                {isDeletingProduct ? "Deleting..." : "Delete Product"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Product;
